import { getLocalTimeZone, today } from "@internationalized/date";
import { z } from "zod/v4";
import { NotitieType } from "#/schema.nswag";
import {
  PAGINATION_SEARCH_DEFAULTS,
  paginationSearchSchema,
} from "#/modules/util.pagination/search.pagination";
import { nuqsBoolean, nuqsCommaArray } from "#/modules/util.pagination/search.nuqs";
import { isApiEnumValue } from "#/modules/util.helpers/zod-enum";

export const isNotitieType = isApiEnumValue(NotitieType);

/** Resolve at call time — must stay lazy, NOT a module-scope constant (a long-lived server
 * process would otherwise freeze "this year" at whatever year it booted in). */
function getDefaultAfgerondAtFrom(): string {
  return today(getLocalTimeZone()).set({ month: 1, day: 1 }).toString();
}
function getDefaultAfgerondAtTo(): string {
  return today(getLocalTimeZone()).set({ month: 12, day: 31 }).toString();
}

export const notitiesSearchSchema = paginationSearchSchema.extend({
  searchTerm: z.string().default(""),
  from: z.string().nullable().default(null),
  to: z.string().nullable().default(null),
  afgerondAtFrom: z.string().default(getDefaultAfgerondAtFrom),
  afgerondAtTo: z.string().default(getDefaultAfgerondAtTo),
  // Comma-joined string or array; invalid members are silently dropped.
  notitieTypes: nuqsCommaArray.transform((types) => types.filter(isNotitieType)),
  includeFullHistory: nuqsBoolean(false),
});

/** Default values for notities search params; values not explicitly set will be omitted from URL. */
export const NOTITIES_SEARCH_DEFAULTS: Omit<NotitiesSearch, "afgerondAtFrom" | "afgerondAtTo"> & {
  afgerondAtFrom: string;
  afgerondAtTo: string;
} = {
  searchTerm: "",
  from: null,
  to: null,
  // NOTE: evaluated once at module load for `stripSearchParams`'s static comparison object; a
  // long-lived process crossing a year boundary may keep an explicit `?afgerondAtFrom=`/`?afgerondAtTo=`
  // in the URL instead of stripping it (cosmetic only — the schema default above is still lazy and
  // always resolves to the current year on each parse).
  afgerondAtFrom: getDefaultAfgerondAtFrom(),
  afgerondAtTo: getDefaultAfgerondAtTo(),
  notitieTypes: [],
  includeFullHistory: false,
  ...PAGINATION_SEARCH_DEFAULTS,
};

export type NotitiesSearch = z.infer<typeof notitiesSearchSchema>;
